"use client";
import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { respondToLegalDocument } from "@/services/api";
import styles from "./LegalConsentModal.module.scss";

interface PendingDoc {
  _id: string;
  type: "privacy" | "terms" | "security";
  title: string;
  content: string;
  version: number;
}

interface Props {
  docs: PendingDoc[];
  userRole: string;
  onDone: () => void;
}

const TYPE_ICON: Record<string, string> = {
  privacy: "🔒",
  terms: "📋",
  security: "🛡️",
};

function renderContent(text: string) {
  return text.split("\n").map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <div key={i} className={styles.spacer} />;
    if (/^\d+\.\s+[A-Z]/.test(trimmed) || /^[A-Z][A-Z\s&—\-]+$/.test(trimmed)) {
      return <div key={i} className={styles.sectionHeading}>{trimmed}</div>;
    }
    return <div key={i} className={styles.bodyLine}>{trimmed}</div>;
  });
}

export default function LegalConsentModal({ docs, userRole, onDone }: Props) {
  const { t } = useLanguage();
  const [responding, setResponding] = useState(false);
  const [done, setDone] = useState<string[]>([]); // ids already responded to

  const canAccept = userRole === "ADMIN";
  const remaining = docs.filter((d) => !done.includes(d._id));
  const current = remaining[0] ?? null;

  const handleRespond = async (action: "accepted" | "skipped") => {
    if (!current) return;
    setResponding(true);
    try {
      await respondToLegalDocument(current._id, action);
      setDone((prev) => [...prev, current._id]);
    } catch {
      // still move forward so user isn't stuck
      setDone((prev) => [...prev, current._id]);
    } finally {
      setResponding(false);
    }
  };

  // all responded to — close immediately, no extra button
  if (!current) {
    onDone();
    return null;
  }

  const totalCount = docs.length;
  const doneCount = done.length;
  const progress = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.shieldIcon}>🔐</div>
            <div>
              <h2>{t("legal.policyUpdate")}</h2>
              <p>
                {totalCount === 1
                  ? t("legal.reviewSingle")
                  : `${doneCount} of ${totalCount} ${t("legal.reviewMultiple")}`}
              </p>
            </div>
          </div>
          {totalCount > 1 && (
            <div className={styles.progressWrap}>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${progress}%` }} />
              </div>
              <span className={styles.progressLabel}>{doneCount}/{totalCount}</span>
            </div>
          )}
        </div>

        {/* Doc tabs when multiple */}
        {totalCount > 1 && (
          <div className={styles.docTabs}>
            {docs.map((d) => (
              <span
                key={d._id}
                className={`${styles.docTab} ${d._id === current._id ? styles.docTabActive : ""} ${done.includes(d._id) ? styles.docTabDone : ""}`}
              >
                {done.includes(d._id) ? "✓" : TYPE_ICON[d.type]} {d.type.charAt(0).toUpperCase() + d.type.slice(1)}
              </span>
            ))}
          </div>
        )}

        {/* Doc content */}
        <div className={styles.docHeader}>
          <span className={styles.typeIcon}>{TYPE_ICON[current.type]}</span>
          <div>
            <h3>{current.title}</h3>
            <span className={styles.versionTag}>{t("legal.version")} {current.version}</span>
          </div>
        </div>

        <div className={styles.contentArea}>
          {renderContent(current.content)}
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          {canAccept ? (
            <>
              <button
                className={styles.acceptBtn}
                onClick={() => handleRespond("accepted")}
                disabled={responding}
              >
                {responding ? t("legal.saving") : `✓ ${t("legal.accept")}`}
              </button>
              <button
                className={styles.skipBtn}
                onClick={() => handleRespond("skipped")}
                disabled={responding}
              >
                {t("legal.skip")}
              </button>
            </>
          ) : (
            <>
              <div className={styles.operatorNotice}>
                {t("legal.operatorNotice")}
              </div>
              <button
                className={styles.skipBtn}
                onClick={() => handleRespond("skipped")}
                disabled={responding}
              >
                {responding ? "..." : t("legal.continue")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
